import React, { useState, useEffect, useContext } from 'react';
import { Databases, Query, ID, Account } from "appwrite";
import client from '../app-user';
import { AuthContext } from '../auth-context';
import LoadingSpinner from '../components/LoadingSpinner';
import styles from '../styles/rfa.module.css';

function getEnvVariable(name: string, defaultValue: string) {
  const value = process.env[name];
  if (value === undefined) return defaultValue;
  return value;
}

const DATABASE_ID = getEnvVariable('REACT_APP_DATABASE_ID', 'database');
const ROSTERED_PLAYER_DATA_COLLECTION_ID = getEnvVariable('REACT_APP_ROSTERED_PLAYER_DATA_COLLECTION_ID', 'roster data');
const RFA_BID_COLLECTION_ID = getEnvVariable('REACT_APP_RFA_BIDS_COLLECTION_ID', 'RFA bids');
const TEAMS_COLLECTION_ID = getEnvVariable('REACT_APP_TEAMS_COLLECTION_ID', 'teams');

type RFAStatus = 'blind_auction' | 'selection_process' | 'off';

interface TeamDocument { $id: string; name: string; sleeperID?: string; UserAuthId?: string; userAuthId?: string; rfaPage?: string; }
interface PlayerDocument { $id: string; salary: number; duration: number; owner: string; name: string; position: string; playerId: string; restricted_free_agent: boolean; }

const databases = new Databases(client);
const account = new Account(client);

const RFAPage = () => {
  const { user } = useContext(AuthContext);
  const [players, setPlayers] = useState<PlayerDocument[]>([]);
  const [submittingPlayer, setSubmittingPlayer] = useState<string | null>(null);
  const [submittedPlayers, setSubmittedPlayers] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<RFAStatus>('blind_auction');
  const [teamSleeperId, setTeamSleeperId] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set());
  const [eligiblePlayers, setEligiblePlayers] = useState<PlayerDocument[]>([]);
  const MAX_SELECTIONS = 3;

  const normalizeStatus = (value?: string): RFAStatus => {
    const v = (value || '').toLowerCase();
    if (v === 'selection' || v === 'selection_process') return 'selection_process';
    if (v === 'disabled' || v === 'off') return 'off';
    if (v === 'bidding' || v === 'blind_auction') return 'blind_auction';
    return 'off';
  };
  const normalizeValue = (value?: string | null) => (value || '').trim().toLowerCase();

  useEffect(() => {
    const load = async () => {
      const sessionId = user.session?.id;
      if (!sessionId) {
        console.log('[RFA Debug] Skipping load because no authenticated session is available yet.');
        setPlayers([]);
        setStatus('off');
        setTeamSleeperId('');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        let allTeams: TeamDocument[] = [];
        let offset = 0;
        const limit = 50;
        while (true) {
          const result = await databases.listDocuments(DATABASE_ID, TEAMS_COLLECTION_ID, [Query.limit(limit), Query.offset(offset)]);
          allTeams = [...allTeams, ...(result.documents as unknown as TeamDocument[])];
          if (result.documents.length < limit) break;
          offset += limit;
        }

        const normalizedSessionId = normalizeValue(sessionId);
        const normalizedUserName = normalizeValue(user.session?.name);
        const userTeamByAuthId = allTeams.find(t => {
          const candidateAuthId = normalizeValue(t.UserAuthId || t.userAuthId);
          return !!candidateAuthId && candidateAuthId === normalizedSessionId;
        });
        const userTeamByName = allTeams.find(t => normalizeValue(t.name) === normalizedUserName);
        const userTeam = userTeamByAuthId || userTeamByName;
        const sleeperId = userTeam?.sleeperID || '';
        const normalizedSleeperId = normalizeValue(sleeperId);
        const currentStatus = normalizeStatus(userTeam?.rfaPage);
        setTeamSleeperId(sleeperId);
        setStatus(currentStatus);

        let allPlayers: PlayerDocument[] = [];
        offset = 0;
        while (true) {
          const result = await databases.listDocuments(DATABASE_ID, ROSTERED_PLAYER_DATA_COLLECTION_ID, [Query.limit(limit), Query.offset(offset)]);
          allPlayers = [...allPlayers, ...(result.documents as unknown as PlayerDocument[])];
          if (result.documents.length < limit) break;
          offset += limit;
        }

        const selectionCandidates = allPlayers.filter(doc => doc.duration === 0);
        const bySleeperOwner = selectionCandidates.filter(doc => normalizedSleeperId && normalizeValue(doc.owner) === normalizedSleeperId);
        setEligiblePlayers(bySleeperOwner);

        const filtered = allPlayers.filter(doc => {
          if (currentStatus === 'selection_process') {
            return doc.duration === 0 && !!normalizedSleeperId && normalizeValue(doc.owner) === normalizedSleeperId;
          }
          return doc.restricted_free_agent === true;
        });

        setPlayers(filtered);
        if (currentStatus === 'selection_process') {
          const preselected = filtered
            .filter(doc => doc.restricted_free_agent === true)
            .map(doc => doc.playerId);
          setSelectedPlayers(new Set(preselected));
        } else {
          setSelectedPlayers(new Set());
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.session?.id, user.session?.name]);

  const submitBid = async (player: PlayerDocument, bidAmount: number, bidDuration: number) => {
    if (!user.session) return;
    setSubmittingPlayer(player.playerId);
    const userDetails = await account.get();
    await databases.createDocument(DATABASE_ID, RFA_BID_COLLECTION_ID, ID.unique(), {
      playerId: player.playerId,
      bidAmount,
      bidDuration,
      playerName: player.name,
      playerPosition: player.position,
      owner: userDetails.name
    });
    setSubmittingPlayer(null);
    setSubmittedPlayers(prev => new Set(prev).add(player.playerId));
  };

  const submitSelectionProcess = async () => {
    try {
      const normalizedSleeperId = normalizeValue(teamSleeperId);
      const teamPlayers = eligiblePlayers.filter(p => normalizeValue(p.owner) === normalizedSleeperId && p.duration === 0);
      await Promise.all(teamPlayers.map(p => databases.updateDocument(DATABASE_ID, ROSTERED_PLAYER_DATA_COLLECTION_ID, p.$id, { restricted_free_agent: false })));
      const selected = eligiblePlayers.filter(p => selectedPlayers.has(p.playerId));
      await Promise.all(selected.map(p => databases.updateDocument(DATABASE_ID, ROSTERED_PLAYER_DATA_COLLECTION_ID, p.$id, { restricted_free_agent: true })));
      alert('Selection process saved.');
    } catch (error) {
      console.error(error);
      alert('Failed to save selection process.');
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (status === 'off') return <div className={styles.container}><h1>Restricted Free Agency</h1><p>This page is currently off.</p></div>;
  const maxSelectionsReached = selectedPlayers.size >= MAX_SELECTIONS;

  return (
    <div className={styles.container}>
      <h1>Restricted Free Agency</h1>
      <ul className={`${styles.playerList} ${status === 'selection_process' ? styles.selectionPlayerList : ''}`}>
        {status === 'selection_process' ? players.map(player => (
          <li key={player.playerId} className={styles.playerItem}>
            <label>
              <input type="checkbox" checked={selectedPlayers.has(player.playerId)} onChange={(e) => setSelectedPlayers(prev => {
                const n = new Set(prev);
                if (e.target.checked) {
                  if (n.size >= MAX_SELECTIONS) {
                    alert(`You can only select up to ${MAX_SELECTIONS} players.`);
                    return n;
                  }
                  n.add(player.playerId);
                } else {
                  n.delete(player.playerId);
                }
                return n;
              })} disabled={!selectedPlayers.has(player.playerId) && maxSelectionsReached} /> <img src={`https://sleepercdn.com/content/nfl/players/thumb/${player.playerId}.jpg`} className={styles.playerPic} alt={`${player.name} headshot`} /> {player.name} - {player.position}
            </label>
          </li>
        )) : players.map(player => (
          <li key={player.playerId} className={styles.playerItem}>
            <span><img src={`https://sleepercdn.com/content/nfl/players/thumb/${player.playerId}.jpg`} className={styles.playerPic} alt={`${player.name} headshot`} /> {player.name} - {player.position}</span>
            <form onSubmit={(e) => { e.preventDefault(); const form = e.currentTarget; submitBid(player, parseFloat((form.elements.namedItem('bidAmount') as HTMLInputElement).value), parseInt((form.elements.namedItem('bidDuration') as HTMLInputElement).value, 10)); form.reset(); }} className={styles.bidForm}>
              <input name="bidAmount" type="number" min="0" max="100" step="0.01" placeholder="Salary" aria-label="Salary" required />
              <input name="bidDuration" type="number" min="1" max="5" step="1" placeholder="Duration" aria-label="Duration" required />
              <button type="submit" disabled={submittingPlayer === player.playerId || submittedPlayers.has(player.playerId)}>{submittingPlayer === player.playerId ? 'Submitting...' : submittedPlayers.has(player.playerId) ? 'Submitted' : 'Submit Bid'}</button>
            </form>
          </li>
        ))}
      </ul>
      {status === 'selection_process' && (
        <div className={styles.selectionActions}>
          <p className={styles.selectionSummary}>Selected {selectedPlayers.size}/{MAX_SELECTIONS} eligible players.</p>
          <button className={styles.selectionButton} onClick={submitSelectionProcess}>Submit Selection</button>
        </div>
      )}
    </div>
  );
};

export default RFAPage;
